import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, KeyRound, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useResetPasswordMutation } from "@/store/ChangPasswordApi";
import { toast } from "sonner";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, role } = location.state || {};

  const [resetPassword, { isLoading: resetLoading }] =
    useResetPasswordMutation();

  const [formData, setFormData] = useState({
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  // ✅ Manage visibility state for both password fields
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    const { otp, newPassword, confirmPassword } = formData;

    if (!otp || !newPassword || !confirmPassword) {
      toast.error("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    const payload = { email, role, otp, newPassword };

    try {
      const res = await resetPassword(payload).unwrap();
      if (res?.success) {
        toast.success("Password reset successful!");
        navigate("/login");
      } else {
        toast.error(res.error.data.message || "Something Went Wrong!!");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error?.data?.message || "Failed to reset password");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-black dark:to-black p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-2">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Reset Password
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-sm">
            Enter the OTP sent to your email and create a new password.
          </p>
          <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            <p>
              <strong>Email:</strong> {email || "Not provided"}
            </p>
            <p>
              <strong>Role:</strong>{" "}
              {role ? role.toUpperCase() : "Not provided"}
            </p>
          </div>
        </div>

        {/* Form Inputs */}
        <div className="space-y-4 text-foreground">
          <div>
            <Label htmlFor="otp">OTP</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                id="otp"
                placeholder="Enter OTP"
                value={formData.otp}
                onChange={handleChange}
                className="pl-10"
              />
            </div>
          </div>

          {/* New Password */}
          <div>
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                id="newPassword"
                type={showPassword.new ? "text" : "password"}
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                className="pl-10 pr-10"
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({ ...prev, new: !prev.new }))
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword.new ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                id="confirmPassword"
                type={showPassword.confirm ? "text" : "password"}
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="pl-10 pr-10"
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    confirm: !prev.confirm,
                  }))
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword.confirm ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            className="w-full h-11 text-base font-semibold"
            disabled={resetLoading}
          >
            {resetLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
        </div>

        <div className="text-center">
          <button
            className="text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
            onClick={() => navigate("/login")}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
